import {
  memo,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  createTodayEventsWhere,
  gameSportcastId,
  gameSportcastIdWhere
} from 'utils/constants/sportsbook/data-fields';
import { ALL_ESPORT_SPORTS } from 'utils/constants/sportsbook/esport';
import { NOT_REAL_SPORTS } from 'utils/constants/sportsbook/sportsbook-data-hierarchy';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  ELayoutTypes,
  EPageTypes,
  TMarketCountResponse
} from 'interfaces/sportsbook';
import {
  ESportsbookLayoutTypes,
  ESportsbookPageTypes
} from 'interfaces/sportsbook-configs';
import {
  IMenuGroupWithSports,
  Sport as TSport
} from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { SportSelect } from './components/SportSelect';
import { getWhat, getWhere } from './helpers';
import { GameListWrapperSkeleton } from 'newcomponents/Shared/sportsbook/SportsbookGameListWrapper/skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useMarketTypes } from 'newhooks/sportsbook/useMarketTypes';
import {
  setClickFromFavorites,
  setMarketType,
  setMarketTypes
} from 'store/actions';
import {
  setGames,
  setMarketsCount,
  setShowNoItemWrapper
} from 'store/actions/new-sportsbook';
import {
  getGames,
  getMarketsCount,
  getRegionCompData,
  getSportGroupingsData
} from 'store/selectors/new-sportsbook';
import {
  getClickFromFavorites,
  getTimeFiltersData,
  getTournamentSportIds
} from 'store/selectors/sport-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { Live } = lazy(
  () =>
    import(/* webpackChunkName: "mobile-live-game-list" */ './__partials/Live')
);

const { Prematch } = lazy(
  () =>
    import(
      /* webpackChunkName: "mobile-prematch-game-list" */ './__partials/Prematch'
    )
);

const { Today } = lazy(
  () =>
    import(
      /* webpackChunkName: "mobile-today-game-list" */ './__partials/Today'
    )
);

const { Team } = lazy(
  () =>
    import(/* webpackChunkName: "mobile-team-game-list" */ './__partials/Team')
);

type TSportResponse = Record<string, TSport>;

type TMarketsCount = {
  [key: string]: number;
};

type TProps = {
  pageType: EPageTypes;
  showHeader?: boolean;
};

const pages = {
  [EPageTypes.prematch]: Prematch,
  [EPageTypes.live]: Live,
  [EPageTypes.today]: Today,
  [EPageTypes.team]: Team
};

export const SportsbookGameListWrapper = memo(
  ({ pageType, showHeader }: TProps): ReactElement => {
    const Page = useMemo(() => pages[pageType], [pageType]);

    const { params } = useSbRouteMatch();
    const { mounted } = useMounted();

    const configs = useSportsbookConfigs();
    const layoutType = useMemo(() => {
      if (
        SpringConfigs.IS_AMERICAN_LAYOUT ||
        configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN
      ) {
        return ELayoutTypes.usa;
      }

      return ELayoutTypes.classic;
    }, []);

    const dispatch = useDispatch();

    const timestamp = useSelector(getTimeFiltersData);
    const tournamentSportIds = useSelector(getTournamentSportIds);
    const clickFromFavorites = useSelector(getClickFromFavorites);

    const groupingData = useSelector(getSportGroupingsData)[
      pageType === EPageTypes.live ? EPageTypes.live : EPageTypes.prematch
    ];

    const isPrematch = pageType === EPageTypes.prematch;

    const getterParam = `${
      isPrematch ? params.competition : params.sport
    }_${pageType}`;

    // if the page type is "team" we set the favorite team id as a region id
    const favoriteTeamId =
      pageType === EPageTypes.team ? Number(params.region) : null;

    const data = useSelector(getGames)[getterParam] || null;
    const marketsCount = useSelector(getMarketsCount)[getterParam] || null;
    const regionCompData = useSelector(getRegionCompData)?.[params.sport];

    const { currentMarketType, callMarketTypes } = useMarketTypes();

    const gameIds = useMemo(() => {
      if (!data) {
        return null;
      }

      const [sport] = Object.values(data);
      const regions = Object.values(sport?.region || {});

      return regions.reduce((result: number[], region) => {
        const competitions = Object.values(region?.competition || {});

        competitions.forEach(competition => {
          sortByOrder(
            Object.values(competition?.game || {}),
            'start_ts'
          ).forEach(game => {
            result.push(Number(game.id));
          });
        });

        return result;
      }, []);
    }, [data]);

    const handleSetData = useCallback(
      (sport: TSportResponse) => {
        if (JSON.stringify(data) !== JSON.stringify(sport)) {
          dispatch(
            setGames({
              [getterParam]: sport
            })
          );
        }
      },
      [data, params.sport, params.competition]
    );

    const updateMarketsCount = useCallback(
      ({ game }: TMarketCountResponse) => {
        const markets = Object.values(game || {}).reduce(
          (result: TMarketsCount, { id, market }) => {
            result[id] = market;

            return result;
          },
          {}
        );

        if (JSON.stringify(marketsCount) !== JSON.stringify(markets)) {
          dispatch(
            setMarketsCount({
              [getterParam]: markets
            })
          );
        }
      },
      [params.sport, params.competition, marketsCount]
    );

    useEffect(() => {
      if (SpringConfigs.MOCKED_DATA) {
        dispatch(
          setMarketType({
            ['Soccer']: { label: 'Match Result', value: 'P1XP2' }
          })
        );

        dispatch(
          setMarketTypes({
            ['Soccer']: [{ label: 'Match Result', value: 'P1XP2' }]
          })
        );
      }

      if (!params.sport) {
        return;
      }

      callMarketTypes();
    }, [params.sport]);

    const currentCompetition = useMemo(() => {
      if (SpringConfigs.MOCKED_DATA || isMobile()) {
        return Math.random();
      }

      if (pageType === EPageTypes.live || pageType === EPageTypes.today) {
        return true;
      }

      if (pageType === EPageTypes.team) {
        return favoriteTeamId;
      }

      if (
        regionCompData === undefined ||
        NOT_REAL_SPORTS.includes(params.category)
      ) {
        return params.competition ?? null;
      }

      return (
        regionCompData &&
        Object.values(regionCompData).find(
          region => region.competition?.[params.competition]
        )?.competition?.[params.competition]
      );
    }, [regionCompData, params.competition, params.sport, favoriteTeamId]);

    const currentCompetitionId =
      typeof currentCompetition === 'object'
        ? currentCompetition?.id
        : currentCompetition;

    useEffect(() => {
      if (
        currentCompetition &&
        currentCompetition !== null &&
        typeof currentCompetition !== 'string' &&
        clickFromFavorites
      ) {
        dispatch(setClickFromFavorites(false));
      }
    }, [currentCompetitionId]);

    const currentMarket =
      typeof currentMarketType === 'object' && currentMarketType !== null
        ? currentMarketType?.value
        : currentMarketType;

    const getGamesEffectDependencyBasedOnMarketTypeAndSport = `${currentMarket}-${params.sport} `;

    useEffect(() => {
      dispatch(
        setShowNoItemWrapper({
          game: false,
          regions: false,
          competitions: false
        })
      );

      if (
        currentCompetition &&
        !Object.keys(currentCompetition)?.length &&
        !EPageTypes.team
      ) {
        dispatch(
          setShowNoItemWrapper({
            game: true,
            regions: true,
            competitions: true
          })
        );
      }

      if (!currentCompetition) {
        if (
          clickFromFavorites &&
          !NOT_REAL_SPORTS.includes(params.category) &&
          currentCompetition !== null
        ) {
          dispatch(
            setShowNoItemWrapper({
              game: true,
              competitions: true
            })
          );

          return;
        }

        return;
      }

      if ((!currentMarketType && currentMarketType !== null) || data?.length) {
        return;
      }

      const rid = RidGenerator.gForSubscribe('GameList');

      const what = getWhat({ pageType });

      const where = getWhere({
        pageType,
        sportAlias: params.sport,
        regionAlias: params.region,
        competitionId: Number(params.competition),
        marketType: currentMarketType?.value || null,
        timestamp: params.category ? {} : timestamp,
        favoriteTeamId,
        tournamentSportIds,
        isUsaLayout:
          configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN ||
          SpringConfigs.IS_AMERICAN_LAYOUT
      });

      if (params.category === ENotRealSports.todayEvents) {
        where.game.start_ts = createTodayEventsWhere().game.start_ts;
      } else if (params.category === ENotRealSports.betBuilder) {
        where.game.sportcast_id = gameSportcastIdWhere;
      }

      if (
        configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN &&
        params.sport === 'Cricket'
      ) {
        what.game = [[...what.game[0], 'stats']];
      }

      executeCommand(
        rid,
        what,
        where,
        (response: { sport: TSportResponse }) => {
          if (mounted.current && response?.sport) {
            handleSetData(response.sport);
          }

          if (
            Object.keys(response?.sport).length === 0 &&
            params.category === ENotRealSports.team
          ) {
            dispatch(
              setShowNoItemWrapper({
                game: true,
                regions: true,
                competitions: true
              })
            );
          }
        },
        true,
        (response: { sport: TSportResponse }) => {
          if (mounted.current && response?.sport) {
            handleSetData(response.sport);
          }
        }
      );

      return () => {
        unsubscribeCommand(rid);
      };
    }, [
      timestamp,
      getGamesEffectDependencyBasedOnMarketTypeAndSport,
      currentCompetitionId,
      clickFromFavorites
    ]);

    useEffect(() => {
      if (SpringConfigs.MOCKED_DATA) {
        return;
      }

      if (gameIds?.length) {
        const originalMarketsCountRid = RidGenerator.gForSubscribe(
          'OriginalMarketsCount'
        );

        const relations = {
          market: '@count',
          game: ['id']
        };

        const where: Record<string, any> = {
          game: {
            id: {
              '@in': gameIds
            }
          }
        };

        if (params.category === ENotRealSports.betBuilder) {
          relations.game.push(gameSportcastId[0]);
          where.game.sportcast_id = gameSportcastIdWhere;
        }

        executeCommand(
          originalMarketsCountRid,
          relations,
          where,
          updateMarketsCount,
          true,
          updateMarketsCount
        );

        return () => {
          if (gameIds?.length) {
            unsubscribeCommand(originalMarketsCountRid);
          }
        };
      } else if (gameIds !== null) {
        dispatch(
          setMarketsCount({
            [getterParam]: {}
          })
        );
      }
    }, [gameIds?.join('')]);

    const groupOfSport = useMemo<IMenuGroupWithSports | null>(() => {
      if (groupingData === null) {
        return null;
      }

      return (
        groupingData?.find(({ sports }) =>
          sports.some(({ alias }) => alias === params.sport)
        ) || null
      );
    }, [groupingData, params.sport]);

    const isLiveWithGroups =
      isMobile() && groupOfSport && pageType === EPageTypes.live;

    const hasSport = ALL_ESPORT_SPORTS.includes(params.sport);
    const isAliasESport = configs.pageType === ESportsbookPageTypes.live;

    return (
      <Suspense
        fallback={
          <GameListWrapperSkeleton
            isLive={pageType === EPageTypes.live}
            showHeader={showHeader}
            showMarketFilterSkeleton={
              configs.spbLayoutType !== ESportsbookLayoutTypes.AMERICAN
            }
            applyAdditionalMargin={isAliasESport && hasSport}
          />
        }
      >
        {isLiveWithGroups ? (
          <div className="CompetitionsBySportsOfGroup__mobile CompetitionsBySportsOfGroup">
            <SportSelect
              pageType={EPageTypes.live}
              groupSports={groupOfSport?.sports}
              drawAlternativeSkeleton
              openDirectionBottom
            />
            <Suspense>
              <Page
                layoutType={layoutType}
                gameIds={gameIds}
                showHeader={groupingData ? false : showHeader}
                sport={params.sport}
              />
            </Suspense>
          </div>
        ) : (
          <Page
            layoutType={layoutType}
            gameIds={gameIds}
            showHeader={showHeader}
            key={getterParam}
          />
        )}
      </Suspense>
    );
  }
);
